import memoize from '@jetbrains/ring-ui-built/components/global/memoize'
import type {CustomAnchorProps, SelectItem} from '@jetbrains/ring-ui-built/components/select/select'
import type {TagType} from '@jetbrains/ring-ui-built/components/tags-list/tags-list'
import type {KeyValue, WritableKeyValue} from '@jetbrains/teamcity-ui/dist/utils'
import type * as React from 'react'

import type {NormalizedProjects} from '../rest/schemata'
import type {
  Agent as RestAgent,
  AgentPool,
  ApprovalInfo,
  Branch,
  Build as RestBuild,
  BuildType,
  CloudImage,
  Comment,
  DownloadInfo,
  EnabledInfo,
  FileChanges,
  Links,
  Pipeline,
  PipelineRun,
  PipelineRunJob,
  Properties,
  Tags,
  User as RestUser,
  User,
} from '../services/rest'

// can't use ElementType, see https://github.com/DefinitelyTyped/DefinitelyTyped/discussions/53864
export type HOC<I, O> = (component: React.ComponentType<I>) => React.ComponentType<O>

declare global {
  type Branded<T, B> = T & {__brand: B}
}
export type Id = number | string
export type BuildId = number
export type PipelineId = Branded<string, 'PipelineId'>
export type BuildTypeId = Branded<string, 'BuildTypeId'>
export type ProjectId = Branded<string, 'ProjectId'>
export type CustomSidebarItemId = Branded<string, 'CustomSidebarItemId'>
export type BuildTypeInternalId = Branded<string, 'BuildTypeInternalId'>
export type ProjectInternalId = Branded<string, 'ProjectInternalId'>
export type UserId = Branded<number, 'UserId'>
export type AgentId = number
export type AgentPoolId = Branded<number, 'AgentPoolId'>
export type AgentTypeId = Branded<number, 'AgentTypeId'>
type CloudProfileId = string
export type CloudInstanceId = string
export type ChangeId = Branded<number, 'ChangeId'>
export type TestId = Branded<string, 'TestId'>
export type TestNameId = string
export type TestOccurrenceId = string
export type ProblemId = string
export type ProblemOccurrenceId = string
type SystemProblemId = string
type InvestigationId = string
export type FederationServerId = string
export type TabId = string
export type RevisionId = string
export type UniqueRevisionId = string // `${vcsRootInstanceId}_${revisionId}`
export type VcsRootInstanceId = string
export type VcsRootId = string
export type TestsTreeNodeId = string
export type ProblemsTreeNodeId = string
export const stringifyId = (id: (Id | null | undefined) | string): string =>
  id != null ? String(id) : ''
export const toBuildId = (key: string | number): BuildId => {
  const result = Number(key)
  // 0 is never an actual BuildId
  return isNaN(result) ? 0 : result
}
export const toChangeId = (key: string | number): ChangeId => Number(key) as ChangeId
export const toPipelineId = (key: string): PipelineId => key as PipelineId
export const toBuildTypeId = (key: string): BuildTypeId => key as BuildTypeId
export const toBuildTypeInternalId = (key: string): BuildTypeInternalId =>
  key as BuildTypeInternalId
export const toProjectId = (key: string): ProjectId => key as ProjectId
export const toProjectInternalId = (key: string): ProjectInternalId => key as ProjectInternalId
export const toUserId = (key: string | number): UserId => Number(key) as UserId
export const toAgentId = (key: string | number): AgentId => Number(key)
export const toAgentTypeId = (key: string | number): AgentTypeId => Number(key) as AgentTypeId
export const toAgentPoolId = (key: string | number): AgentPoolId => Number(key) as AgentPoolId
export const toTestsTreeNodeId = (key: string | number): TestsTreeNodeId => String(key)
export const toFederationServerId = (key: string): FederationServerId => key
export const toCustomSidebarItemId = (key: string): CustomSidebarItemId =>
  key as CustomSidebarItemId
export const toTabId = (key: string): TabId => String(key)
export const toProblemId = (key: string): ProblemId => String(key)
export const toProblemOccurrenceId = (key: string): ProblemOccurrenceId => String(key)
export const toSystemProblemId = (key: string): SystemProblemId => String(key)
export const toInvestigationId = (key: string): InvestigationId => String(key)
export const toTestId = (key: string | number): TestId => String(key) as TestId
export const toTestOccurrenceId = (key: string): TestOccurrenceId => String(key)
export const toRevisionId = (key: string): RevisionId => String(key)
export const toVcsRootInstanceId = (key: string | number): VcsRootInstanceId => String(key)
export const toVcsRootId = (key: string): VcsRootId => String(key)
export const ROOT_PROJECT_ID: ProjectId = toProjectId('_Root')
export const ROOT_PROJECT_INTERNAL_ID: ProjectInternalId = toProjectInternalId('_Root')
export const ROOT_PROJECT_NAME = '<Root project>'
export type NodeType = 'project' | 'bt' | 'build' | 'job'
export type NodeGroup = 'favorites' | 'all' | 'search' | 'none'
export type LinkType = 'webView' | 'webEdit' | 'webViewSettings'
export type Inexact<T> = T & Record<PropertyKey, unknown>
type WebEntity = {
  description?: string
  name?: string
  webUrl?: string
}
type InexactWebEntity = Inexact<WebEntity>
export type WebLinks = {
  links?: Links | null
}
type WebEntityWithLinks = WebEntity & WebLinks
type InexactWebEntityWithLinks = Inexact<WebEntityWithLinks>
export type TypeOfBuildType = 'composite' | 'deployment' | 'regular'
type EntityParameters = {
  parameters?: Properties
  pauseComment?: CommentInfo
}
export type InexactEntityParameters = Inexact<EntityParameters>
export type EntityDescription = {
  description?: string | undefined
}
export type EntityProjectsIdsList = ReadonlyArray<{
  id: ProjectId
}>
export type EntityArchivedSubprojectsIds = {
  projects?: {
    project: EntityProjectsIdsList
  }
}
type BuildTypeCollection = {
  readonly buildType: BuildType[]
}
type BuildTypeWithDetailsCollection = {
  buildType: BuildType[]
}
type PipelineWithDetailsCollection = {
  count?: number
  readonly pipeline: Pipeline[]
}
export type Template = {
  id: BuildTypeId
  name: string
}
type BaseProject<P> = WebEntity & {
  id: ProjectId
  internalId: ProjectInternalId
  parentProjectId?: ProjectId
  virtual?: boolean
  holdsPipeline?: boolean
  archived: boolean
  readOnlyUI?: {
    value: boolean
  }
  templates?: {
    buildType: Template[]
  }
  ancestorProjects?: {
    project: P[]
  }
}
type Project = BaseProject<BaseProject<never>> & {
  readonly buildTypes?: BuildTypeCollection
  readonly pipelines?: PipelineWithDetailsCollection
}
export type ProjectWithDetails = BaseProject<BaseProject<never>> &
  WebLinks &
  EntityParameters & {
    buildTypes?: BuildTypeWithDetailsCollection
    pipelines?: PipelineWithDetailsCollection
  }
export type NormalizedProject = BaseProject<ProjectId> &
  WebLinks & {
    buildTypes?: {
      buildType: Array<BuildTypeId>
    }
    projects?: {
      project:
        | Array<{
            id: ProjectId
          }>
        | undefined
    }
    pipelines?: {
      pipeline: Array<PipelineId>
    }
  }
export type SidebarProject = BaseProject<ProjectId> &
  WebLinks & {
    buildTypes?: BuildTypeWithDetailsCollection
    pipelines?: PipelineWithDetailsCollection
  }
export type SelectedBranch = Branch & {wildcard?: boolean}
export type BranchItem = SelectItem<{
  label?: string | null
  selectedLabel?: React.ReactNode
  key: string
  branch?: SelectedBranch
}>
export type OSType = 'Windows' | 'macOS' | 'Linux' | 'Solaris' | 'FreeBSD' | 'Unix' | 'Unknown'
export type AvatarSize = `urlToSize${number}`
export const getAvatarSizeKey = (size: number): AvatarSize => `urlToSize${size}`
export type CommentInfo = Comment
type CloudProfile = {
  id: CloudProfileId
  name?: string
  projectId?: ProjectId
}
type BaseCloudImage = {
  name: string
  agentPoolId: AgentPoolId
  agentTypeId: AgentTypeId
  profile?: CloudProfile
}
type CloudInstance = {
  id?: 'id:fake0,imageId:jb-hosted,profileId:tc-1'
  image?: CloudImage
  agent?: {
    id: AgentId
    typeId?: AgentTypeId
    pool?: AgentPool
  }
}
export type NormalizedCloudImage = BaseCloudImage &
  Environment & {
    agentIds: Array<AgentId>
  }
export type CloudImagesHash = KeyValue<AgentTypeId, NormalizedCloudImage>
type Environment = {
  osType?: OSType | undefined
  osName?: string | undefined
}
type BaseAgentPreview<B> = {
  id: AgentId
  typeId: AgentTypeId
  name: string
  ip?: string
  enabled?: boolean
  connected: boolean
  authorized?: boolean
  pool: AgentPool
  build?: B | undefined
  removed?: boolean
}
export type NormalizedAgentPreview = BaseAgentPreview<BuildId> & {
  osType: OSType | undefined
}
export type WritableAgentPreviewsHash = WritableKeyValue<
  AgentId,
  NormalizedAgentPreview | null | undefined
>
export type AgentPreviewsHash = Readonly<WritableAgentPreviewsHash>
type BaseAgent<B> = WebEntityWithLinks &
  BaseAgentPreview<B> & {
    cloudInstanceId?: CloudInstanceId
    enabledInfo?: EnabledInfo
    authorizedInfo?: EnabledInfo
    uptodate?: boolean
    upgrading?: boolean
    connected?: boolean
    version?: string
    outdated?: boolean
    currentAgentVersion?: string
    pluginsOutdated?: boolean
    javaOutdated?: boolean
    lastActivityTime?: string
    disconnectionComment?: string
    deleted?: boolean
    // details
    connectedSince?: string | undefined
    host?: string
    cpuRank?: number
    port?: number
  }
export type AgentDetails = Pick<RestAgent, 'host' | 'port' | 'protocol' | 'cpuRank'> & {
  connectedSince?: string | undefined
}
export type NormalizedAgent = BaseAgent<BuildId> & Environment
export type AgentRequestOptions = {
  authorizedInfo?: boolean
  disconnectionComment?: boolean
  lastActivityTime?: boolean
  enabledInfo?: boolean
  cloudInfo?: boolean
  details?: boolean
  tabs?: boolean
}
type ArchivedOption = 'any' | 'false' | 'true'
export type RequestOptionsParams = {
  withPipelines?: boolean
  withLinks?: boolean
  withLinksAvailability?: boolean
  withVirtual?: boolean
  withHoldsPipeline?: boolean
  withBuildTypes?: boolean
  archived?: ArchivedOption
  withLowNesting?: boolean
  withArchivedSubprojectsIds?: boolean
  federationServerId?: FederationServerId
  withParameters?: boolean
  withDescription?: boolean
  withQueuedInfo?: boolean
  withRunningInfo?: boolean
  withShortProjectDetails?: boolean
  withShortBuildDetails?: boolean
  withBuildTypeDetails?: boolean
  withSnapshotDependencies?: boolean
  withDownloadedArtifactsFrom?: BuildId | null
  withPauseComment?: boolean
  withProgress?: boolean
  withAncestorProjects?: boolean
  withOnlyArchivedAncestorProjects?: boolean
  fetchCount?: number
  includeRoot?: boolean
  customEndpoint?: string
  essential?: boolean
  cache?: boolean
  withBuildDetails?: boolean
  withChanges?: boolean
  withPipelineRuns?: boolean
  withAnchorBuild?: boolean
  isSidebar?: boolean
}

export type RunningInfo = {
  percentageComplete?: number
  elapsedSeconds: number
  estimatedTotalSeconds?: number
  leftSeconds?: number
  lastActivityTime?: string
  probablyHanging?: boolean
  outdated?: boolean
  outdatedReasonBuild?: InexactWebEntityWithLinks & {
    id: BuildId
    number: string | undefined
  }
}
export type Countable = {
  count?: number
}
export type InexactCountable = Inexact<Countable>
export type CompatibleAgent = {
  id: BuildId
  compatibleAgents?: InexactCountable
  compatibleCloudImages?: InexactCountable
}
export type Tag = {
  name: string
  private: boolean
  key?: string
  value?: string
}
export type BuildStatus =
  | 'UNKNOWN'
  | 'SUCCESS' // NORMAL
  | 'WARNING'
  | 'FAILURE'
  | 'ERROR'
export type BuildState = 'queued' | 'running' | 'finished' | 'deleted' | 'unknown'
export type BuildArtifacts = {
  id: BuildId
  artifacts?: InexactCountable
}
type BuildTriggeredBuild = InexactWebEntityWithLinks & {
  id: BuildId | undefined
  number?: string | undefined
}
type BaseBuildTriggered<BT> = {
  date?: string
  displayText?: string | undefined
  build?: BuildTriggeredBuild
  buildType?: BT | undefined
  user?: RestUser
}
export type BuildTriggered = BaseBuildTriggered<BuildType>
export type NormalizedBuildTriggered = BaseBuildTriggered<BuildTypeId>
type ChangeStatus = {
  newFailedTests?: number
  otherFailedTests?: number
  totalProblems?: number
  cancelledBuilds?: number
  finishedBuilds?: number
  failedBuilds?: number
  runningBuilds?: number
  queuedBuildsCount?: number
  pendingBuildTypes?: number
  runningSuccessfullyBuilds?: number
  successfulBuilds?: number
  compilationErrorBuilds?: {
    count?: number
  }
  criticalBuilds?: {
    count?: number
    build?: Array<Build>
  }
  newTestsFailedBuilds?: {
    count?: number
    build?: Array<Build>
  }
  notCriticalBuilds?: {
    count?: number
    build?: Array<Build>
  }
}
type ChangeBranch = {
  name: string
  default?: boolean
}

export type BaseChange<V, C = BaseChange<V, {}>> = {
  id: ChangeId
  type?: 'SNAPSHOT_DEPENDENCY_VCS_CHANGE' | 'VCS_CHANGE'
  username?: string
  commiter?: {
    users: {
      user: User[]
    }
  }
  version?: RevisionId
  parentRevisions?: {
    item?: Array<RevisionId>
  }
  mergedInfo?: {
    changes?: {
      change?: Array<C>
    }
    branches?: {
      branch?: Array<ChangeBranch>
    }
  }
  personal?: boolean
  comment?: string
  status?: ChangeStatus
  date?: string
  webUrl?: string
  isArtifactChange?: boolean
  vcsRootInstance?: V | undefined
  snapshotDependencyLink?: {
    build?: Build
    buildType?: BuildType
    buildTypeBranch?: string
  }
  storesProjectSettings?: boolean
  files?: FileChanges
}
export type NormalizedChange = BaseChange<VcsRootInstanceId, ChangeId>
export type VcsRoot = {
  id?: VcsRootId
  vcsName?: string
  name?: string
  project?: Project
}
type BaseVcsRootInstance<V> = {
  id: VcsRootInstanceId
  'vcs-root'?: V
  properties?: Properties
}
export type NormalizedVcsRootInstance = BaseVcsRootInstance<VcsRootId>
type VcsRootInstance = BaseVcsRootInstance<VcsRoot>

type VcsLabelStatus = 'SUCCESSFUL_SET' | 'FAILED'
export type VcsLabel = {
  text?: string
  failureReason?: string
  status?: VcsLabelStatus
  'vcs-root-instance'?: {
    'vcs-root': {
      id: VcsRootId
    }
  }
}
export type Change = BaseChange<VcsRootInstance>
export const getUniqueRevisionId = (
  vcsRootInstanceId: VcsRootInstanceId,
  revisionId: RevisionId,
): UniqueRevisionId => `${stringifyId(vcsRootInstanceId)}_${stringifyId(revisionId)}`
type BaseRevision<V> = {
  version: RevisionId
  vcsBranchName: string
  'vcs-root-instance': V
}
export type NormalizedRevision = BaseRevision<VcsRootInstanceId>
export type Revision = BaseRevision<VcsRootInstance>
type Changes<C> = InexactCountable & {
  change?: Array<C>
}
type BaseBuildChanges<C, B> = {
  id: BuildId
  changes?: Changes<C>
  artifactDependencyChanges?: InexactCountable & {
    buildChange?: Array<{
      nextBuild?: B
    }>
  }
}
export type BuildChangesRevisions = {
  id: BuildId
  versionedSettingsRevision?: Revision | undefined
  revisions:
    | {
        revision: Array<Revision> | undefined
      }
    | undefined
  vcsLabels: VcsLabel[]
}
type DownloadedArtifacts<T extends DownloadInfo = DownloadInfo> = {
  downloadedArtifacts: {
    downloadInfo: T[]
  }
}
export type MatrixDependencyParameter = {
  name?: string
  value?: string
}
type BaseMatrixDependency<B> = {
  build?: B
  parameters?: {
    entry?: MatrixDependencyParameter[]
  }
}
export type MatrixDependency = BaseMatrixDependency<NormalizedBuild>
export type NormalizedMatrixDependency = BaseMatrixDependency<BuildId>
export type MatrixParameterValue = {
  value: string
  label?: string
}
export type MatrixParameter<T = MatrixParameterValue> = {
  name?: string
  value?: T[]
}
type BaseMatrixConfiguration<B> = {
  enabled?: boolean
  dependencies?: {
    dependency?: BaseMatrixDependency<B>[]
  }
  parameters?: {
    parameters?: MatrixParameter[]
  }
}
type Executor = {
  name?: string
}
type BaseBuild<BT, C, R, A, B, P, J> = InexactWebEntity &
  WebLinks &
  BaseBuildChanges<C, B> &
  BuildArtifacts &
  CompatibleAgent &
  Partial<DownloadedArtifacts> & {
    id: BuildId
    agent?: A
    executor?: Executor
    plannedAgent?: A
    comment?: CommentInfo
    statusChangeComment?: CommentInfo | undefined
    branchName?: string
    defaultBranch?: boolean
    buildType: BT
    queuedDate?: string
    startDate?: string | null
    finishDate?: string
    number?: string
    status?: BuildStatus
    detachedFromAgent?: boolean
    finishOnAgentDate?: string
    state?: BuildState
    canceledInfo?: CommentInfo | null | undefined
    failedToStart?: boolean
    statusText?: string
    personal?: boolean
    waitReason?: string
    startEstimate?: string
    finishEstimate?: string
    user?: User
    approvalInfo?: Pick<ApprovalInfo, 'status'>
    'running-info'?: RunningInfo | undefined
    tags?: Tags
    pinned?: boolean
    pinInfo?: CommentInfo | undefined
    'artifact-dependencies'?: InexactCountable & {
      build?: Array<{
        id: BuildId
      }>
    }
    'snapshot-dependencies'?: InexactCountable & {
      build?: Array<RestBuild>
    }
    versionedSettingsRevision?: R | undefined
    revisions?:
      | {
          revision: Array<R> | undefined
        }
      | undefined
    queuePosition?: number
    triggered?: BaseBuildTriggered<BT>
    history?: boolean
    limitedChangesCount?: number
    composite?: boolean
    parallelized?: boolean
    customization?: {
      changes?: {}
      parameters?: {}
      artifactDependencies?: {}
      dependencies?: {}
    }
    testOccurrences?: Countable
    delayedByBuild?: B
    related?: {
      builds?: {
        build?: B[]
      }
    }
    firstBuildWithSameChanges?: B
    matrixConfiguration?: BaseMatrixConfiguration<B>
    pipelineRun?: P
    pipelineRunJob?: J
    anchorBuild?: B
  }

export type NormalizedBuild = BaseBuild<
  BuildTypeId,
  ChangeId,
  RevisionId,
  NormalizedAgent,
  BuildId,
  string,
  string
>
type BuildAgent = WebEntityWithLinks & {
  name: string
  id?: AgentId
  environment: Environment
  typeId: AgentTypeId
  connected?: boolean
  pool?: AgentPool
}

type RelatedBuild = BaseBuild<BuildType, never, never, never, never, PipelineRun, PipelineRunJob>
export type Build = BaseBuild<
  BuildType,
  Change,
  Revision,
  BuildAgent,
  RelatedBuild,
  PipelineRun,
  PipelineRunJob
>

export type NormalizedPipelineRunJob = {
  id: string
  name: string
  build: Build
  pipelineRun: string
}
export type NormalizedPipelineRun = {
  number: string
  build: Build
  pipeline: PipelineId
  jobs: PipelineRunJob
}
export type AgentPreview = BaseAgentPreview<{
  id: BuildId
}> & {
  environment: Environment
}
export type Agent = BaseAgent<Build> &
  AgentDetails & {
    environment: Environment
  }
export type AgentResponse = Agent & {
  cloudInstance?: CloudInstance
}
export enum Permission {
  RUN_BUILD = 'run_build',
  CANCEL_BUILD = 'cancel_build',
  CANCEL_ANY_PERSONAL_BUILD = 'cancel_any_personal_build',
  TAG_BUILD = 'tag_build',
  CHANGE_OWN_PROFILE = 'change_own_profile',
  PIN_UNPIN_BUILD = 'pin_unpin_build',
  AUTHORIZE_AGENT = 'authorize_agent',
  REORDER_BUILD_QUEUE = 'reorder_build_queue',
  COMMENT_BUILD = 'comment_build',
  ASSIGN_INVESTIGATION = 'assign_investigation',
  CHANGE_SERVER_SETTINGS = 'change_server_settings',
  VIEW_BUILD_CONFIGURATION_SETTINGS = 'view_build_configuration_settings',
  VIEW_BUILD_RUNTIME_DATA = 'view_build_runtime_data',
  MANAGE_SERVER_LICENSES = 'manage_server_licenses',
  MANAGE_BUILD_PROBLEMS = 'manage_build_problems',
  EDIT_PROJECT = 'edit_project',
  EDIT_VCS_MODIFICATION = 'edit_vcs_modification',
  CREATE_SUB_PROJECT = 'create_sub_project',
  VIEW_USER_PROFILE = 'view_user_profile',
  MANAGE_AGENT_POOLS = 'manage_agent_pools',
  MANAGE_AGENT_CLOUDS = 'manage_agent_clouds',
  PAUSE_ACTIVATE_BUILD_CONFIGURATION = 'pause_activate_build_configuration',
  CHANGE_CLEANUP_RULES = 'change_cleanup_rules',
  REMOVE_BUILD = 'remove_build',
  CHANGE_USER = 'change_user',
  EDIT_VERSIONED_SETTINGS = 'edit_versioned_settings',
}

export type ChangesRequestOptions = {
  withUpdatePager?: boolean
  withFiles?: boolean
  withStatus?: boolean
}

export type Fetchable<T> = {
  inited: boolean
  loading: boolean
  backgroundLoading: boolean
  ready: boolean
  error?: Error | null | undefined
  data: T
}
export type FederationServerData = {
  authorized: boolean
  projects?: NormalizedProjects
}
export type ProjectPathItem = BaseProject<ProjectId> & WebLinks
export type PipelineRunJobPathItem = NormalizedPipelineRunJob & {pipelineId: PipelineId}
export type PipelineRunJobBatchPathItem = {
  id: BuildTypeId
  name: string
  batchId: string
  jobId: string
  runId: string
  pipelineId: PipelineId
}
export type PathItem =
  | ProjectPathItem
  | BuildType
  | Pipeline
  | PipelineRunJobPathItem
  | PipelineRunJobBatchPathItem
export type FullPath = ReadonlyArray<PathItem>
type ProjectStatusRequest = {
  id?: ProjectId
  type: 'project'
  branch?: Branch | null | undefined
  projectPermissions?: {
    canCreateSubProject: boolean
    canEditProject: boolean
    canCreatePipelines: boolean
    pipelineCreationDisabledReason?: string
  }
}
type BuildTypeStatusRequest = {
  id?: BuildTypeId
  type: 'bt'
  branch?: Branch | null | undefined
}
type PipelineStatusRequest = {
  id?: PipelineId
  type: 'pipeline'
  branch?: Branch | null | undefined
}
export type StatusRequest = ProjectStatusRequest | BuildTypeStatusRequest | PipelineStatusRequest
export const getProjectStatusRequest: (
  projectId?: ProjectId,
  branch?: Branch | null,
) => StatusRequest = (id, branch) => ({
  type: 'project',
  id,
  branch,
})
export const getBuildTypeStatusRequest: (
  buildTypeId?: BuildTypeId,
  branch?: Branch | null,
) => StatusRequest = (id, branch) => ({
  type: 'bt',
  id,
  branch,
})
export const getPipelineStatusRequest: (
  pipelineId?: PipelineId,
  branch?: Branch | null,
) => StatusRequest = (id, branch) => ({
  type: 'pipeline',
  id,
  branch,
})

type StatusRequestArgs =
  | {
      type: 'project'
      id: ProjectId
      branch?: Branch | null
    }
  | {
      type: 'bt'
      id?: BuildTypeId
      branch?: Branch | null
    }
  | {
      type: 'pipeline'
      id?: PipelineId
      branch?: Branch | null
    }
export function getStatusRequest({type, id, branch}: StatusRequestArgs): StatusRequest {
  switch (type) {
    case 'project':
      return getProjectStatusRequest(id, branch)
    case 'pipeline':
      return getPipelineStatusRequest(id, branch)
    default:
      return getBuildTypeStatusRequest(id, branch)
  }
}

export type SystemProblem = {
  id: SystemProblemId
  type?: string | undefined
  title?: string
  description?: string | undefined
  problemDetails?: string | undefined
  webSpecifics?:
    | {
        solutionLink: {
          link: string
          title: string | undefined
        }
        caption: string
        additionalData: string | undefined
      }
    | undefined
}
type FailedCounts = {
  inLatestFinished?: number
  inRunning?: Array<number>
}
export type ProjectOrBuildTypeStatusBase = {
  status: 'SUCCESS' | 'FAILURE' | 'UNKNOWN'
  pending?: number
  pendingLimitExceeded?: boolean | undefined
  hasOwnPending?: boolean
  running?: number
  failing?: number
  queued?: number
  systemProblems?: Array<SystemProblem>
  additionalProblems?: Array<SystemProblem>
  investigation?: 'TAKEN' | 'FIXED' | 'GIVEN_UP' | 'NONE'
  failedCounts?: FailedCounts
}
export type ProjectOrBuildTypeStatus =
  | (ProjectStatusRequest & ProjectOrBuildTypeStatusBase)
  | (BuildTypeStatusRequest & ProjectOrBuildTypeStatusBase)
  | (PipelineStatusRequest & ProjectOrBuildTypeStatusBase)
export type ExpandState = {
  type: 'project'
  id?: ProjectId
  expandState: 'EXPANDED' | 'COLLAPSED'
}
export type Sorting = {
  dimension: string
  descending: boolean
}
export type PinHandlerArgsAjax = {
  pin: boolean
  pinComment: string | undefined
  tags: ReadonlyArray<Tag>
  applyToChainBuilds: boolean
}
export type TitleId = '_all_projects_title_' | '_favorite_projects_title_'
export const FAVORITES_TITLE_ID: TitleId = '_favorite_projects_title_'
export const ALL_PROJECTS_TITLE_ID: TitleId = '_all_projects_title_'

export type UrlExtension<Options extends {}> = {
  name: string
  kind?: string
  serverUrl?: string
  endpoint: string
  options?: Options
}
export type RouteAvailabilityResponse = {
  status: string
  statusCode: number
  message: string
}
type TabTitlesEnum = KeyValue<TabId, string>

export const ClassicUIProjectTabNamesEnum = {
  projectOverview: toTabId('projectOverview') as TabId,
  projectChangeLog: toTabId('projectChangeLog') as TabId,
  stats: toTabId('stats') as TabId,
  problems: toTabId('problems') as TabId,
  investigations: toTabId('investigations') as TabId,
  mutedProblems: toTabId('mutedProblems') as TabId,
  projectBuildChains: toTabId('projectBuildChains') as TabId,
  flakyTests: toTabId('flakyTests') as TabId,
}
export const ClassicUIBuildTypeTabNamesEnum = {
  buildTypeBranches: toTabId('buildTypeBranches') as TabId,
  buildTypeStatusDiv: toTabId('buildTypeStatusDiv') as TabId,
  buildTypeHistoryList: toTabId('buildTypeHistoryList') as TabId,
  buildTypeChangeLog: toTabId('buildTypeChangeLog') as TabId,
  buildTypeIssueLog: toTabId('buildTypeIssueLog') as TabId,
  buildTypeStatistics: toTabId('buildTypeStatistics') as TabId,
  compatibilityList: toTabId('compatibilityList') as TabId,
  pendingChangesDiv: toTabId('pendingChangesDiv') as TabId,
  buildTypeChains: toTabId('buildTypeChains') as TabId,
  buildTypeSettings: toTabId('buildTypeSettings') as TabId,
  maven: toTabId('maven') as TabId,
  agentBrowse: toTabId('agentBrowse') as TabId,
}
export const ClassicUIBuildTabNamesEnum = {
  queuedBuildOverviewTab: toTabId('queuedBuildOverviewTab') as TabId,
  queuedBuildCompatibilityTab: toTabId('queuedBuildCompatibilityTab') as TabId,
  buildResultsDiv: toTabId('buildResultsDiv') as TabId,
  testsInfo: toTabId('testsInfo') as TabId,
  buildChangesDiv: toTabId('buildChangesDiv') as TabId,
  dependencies: toTabId('dependencies') as TabId,
  buildParameters: toTabId('buildParameters') as TabId,
  buildIssues: toTabId('buildIssues') as TabId,
  artifacts: toTabId('artifacts') as TabId,
  perfmon: toTabId('perfmon') as TabId,
  buildLog: toTabId('buildLog') as TabId,
  buildUsedResources: toTabId('buildUsedResources') as TabId,
  dockerBuildInfo: toTabId('dockerBuildInfo') as TabId,
  mavenBuildInfo: toTabId('mavenBuildInfo') as TabId,
  Duplicator: toTabId('Duplicator') as TabId,
  Inspection: toTabId('Inspection') as TabId,
  coverage_go: toTabId('coverage_go') as TabId,
  coverage_dotnet: toTabId('coverage_dotnet') as TabId,
  coverage_idea: toTabId('coverage_idea') as TabId,
  coverage_emma: toTabId('coverage_emma') as TabId,
  coverage_jacoco: toTabId('coverage_jacoco') as TabId,
  fxcopReportTab: toTabId('fxcopReportTab') as TabId,
}

export const ProjectPageTabNamesEnum = {
  ...ClassicUIProjectTabNamesEnum,
  CHANGE_LOG: toTabId('changeLog') as TabId,
  OVERVIEW: toTabId('overview') as TabId,
  STATISTICS: toTabId('stats') as TabId,
  TEST_DETAILS: toTabId('testDetails') as TabId,
  INVESTIGATIONS: toTabId('investigations') as TabId,
  PROBLEMS: toTabId('problems') as TabId,
}
export const BuildTypePageTabNamesEnum = {
  ...ClassicUIBuildTypeTabNamesEnum,
  CHANGE_LOG: toTabId('changeLog') as TabId,
  OVERVIEW: toTabId('overview') as TabId,
  STATISTICS: toTabId('buildTypeStatistics') as TabId,
  PENDING_CHANGES: toTabId('pendingChanges') as TabId,
  CHAINS: toTabId('chains') as TabId,
  PROBLEMS: toTabId('problems') as TabId,
}
export const defaultBuildTypePageTabName = BuildTypePageTabNamesEnum.OVERVIEW

export const BuildPageTabNamesEnum = {
  ...ClassicUIBuildTabNamesEnum,
  ARTIFACTS: toTabId('artifacts') as TabId,
  OVERVIEW: toTabId('overview') as TabId,
  TESTS: toTabId('tests') as TabId,
  CHANGES: toTabId('changes') as TabId,
  LOG: toTabId('log') as TabId,
  DEPENDENCIES: toTabId('dependencies') as TabId,
  PARAMETERS: toTabId('buildParameters') as TabId,
}
export const OverviewAgentTabNamesEnum = {
  SUMMARY: toTabId('summary') as TabId,
  HISTORY: toTabId('history') as TabId,
  COMPATIBLE_CONFIGUTATIONS: toTabId('compatible-configurations') as TabId,
  BUILD_RUNNERS: toTabId('build-runners') as TabId,
  LOGS: toTabId('logs') as TabId,
  PARAMETERS: toTabId('parameters') as TabId,
}
export const OverviewAgentsTabNamesEnum = {
  ALL_AGENTS: toTabId('all-agents') as TabId,
  PARAMETERS_REPORT: toTabId('agentsParametersReport') as TabId,
  AGENTS_STATISTICS: toTabId('agentsStatisticsTab') as TabId,
  AGENT_PUSH: toTabId('agent.push') as TabId,
}
export const OverviewCloudImageTabNamesEnum = {
  SUMMARY: toTabId('summary') as TabId,
  HISTORY: toTabId('history') as TabId,
  COMPATIBLE_CONFIGUTATIONS: toTabId('compatible-configurations') as TabId,
  BUILD_RUNNERS: toTabId('build-runners') as TabId,
  AGENT_PARAMETERS: toTabId('agent-parameters') as TabId,
}
export const OverviewAgentPoolTabNamesEnum = {
  AGENTS: toTabId('agents') as TabId,
  PROJECTS: toTabId('projects') as TabId,
  CLOUD_IMAGES: toTabId('cloud-images') as TabId,
}

export const ProjectPageTabTitlesEnum: TabTitlesEnum = {
  [ProjectPageTabNamesEnum.OVERVIEW]: 'Overview',
  [ProjectPageTabNamesEnum.CHANGE_LOG]: 'Change Log',
  [ProjectPageTabNamesEnum.PROBLEMS]: 'Problems',
}
export const BuildTypePageTabTitlesEnum: TabTitlesEnum = {
  [BuildTypePageTabNamesEnum.OVERVIEW]: 'Overview',
  [BuildTypePageTabNamesEnum.PENDING_CHANGES]: 'Pending Changes',
  [BuildTypePageTabNamesEnum.CHAINS]: 'Chains',
  [BuildTypePageTabNamesEnum.CHANGE_LOG]: 'Change Log',
}
export const BuildPageTabTitlesEnum: TabTitlesEnum = {
  [BuildPageTabNamesEnum.ARTIFACTS]: 'Artifacts',
  [BuildPageTabNamesEnum.OVERVIEW]: 'Overview',
  [BuildPageTabNamesEnum.TESTS]: 'Tests',
  [BuildPageTabNamesEnum.CHANGES]: 'Changes',
  [BuildPageTabNamesEnum.LOG]: 'Build Log',
  [BuildPageTabNamesEnum.DEPENDENCIES]: 'Dependencies',
}
export const JobTabTitlesEnum: TabTitlesEnum = {
  [ClassicUIBuildTabNamesEnum.buildResultsDiv]: 'Problems',
}

export const ClassicUIAgentTabNamesEnum: KeyValue<TabId, TabId> = {
  agentSummary: OverviewAgentTabNamesEnum.SUMMARY,
  agentHistory: OverviewAgentTabNamesEnum.HISTORY,
  agentCompatibleConfigurations: OverviewAgentTabNamesEnum.COMPATIBLE_CONFIGUTATIONS,
  agentBuildRunners: OverviewAgentTabNamesEnum.BUILD_RUNNERS,
  agentLogs: OverviewAgentTabNamesEnum.LOGS,
  agentParameters: OverviewAgentTabNamesEnum.PARAMETERS,
}
export const ClassicUICloudImageTabNamesEnum: KeyValue<string, TabId> = {
  agentTypeSummary: OverviewCloudImageTabNamesEnum.SUMMARY,
  agentHistory: OverviewCloudImageTabNamesEnum.HISTORY,
  agentCompatibleConfigurations: OverviewCloudImageTabNamesEnum.COMPATIBLE_CONFIGUTATIONS,
  agentBuildRunners: OverviewCloudImageTabNamesEnum.BUILD_RUNNERS,
  agentParameters: OverviewCloudImageTabNamesEnum.AGENT_PARAMETERS,
}

export enum SakuraUIEnabledEnum {
  ENABLE_ALL = 'enableAll',
  ENABLE_ALL_SOFT = 'enableAllSoft',
  ENABLE_DEFAULT = 'enableDefault',
  DISABLE_ALL = 'disableAll',
  DISABLE_DEFAULT = 'disableDefault',
}
export enum DialogType {
  COMMENT = 'comment',
  TAGS = 'tags',
  PIN = 'pin',
  EDIT_PIN_COMMENT = 'editPinComment',
  COMPARE = 'compare',
  EDIT_SIDEBAR = 'editSidebar',
  FAVORITE_AGENT_POOLS = 'favoriteAgentPools',
  INVESTIGATION = 'investigation',
  INVESTIGATION_HISTORY = 'investigationHistory',
  METADATA_DETAIL_POPUP = 'metadataDetailPopup',
  MUTE = 'mute',
  BUILD_STATUS_WIDGET = 'buildStatusWidget',
}
export type AgentFilter = {
  id?: AgentId
  typeId?: AgentTypeId
}
export type ProjectOrBuildTypeNode =
  | {
      nodeType: 'all'
      id?: null
      group?: NodeGroup
    }
  | {
      nodeType: 'project'
      id?: ProjectId
      fullPath?: string
      group?: NodeGroup
      parentId?: ProjectId | null | undefined
      internalId?: ProjectInternalId | null | undefined
    }
  | {
      nodeType: 'bt'
      id: BuildTypeId
      fullPath?: string
      group?: NodeGroup
      parentId?: ProjectId | null | undefined
      parentInternalId?: ProjectInternalId | null | undefined
      internalId?: BuildTypeInternalId | null | undefined
    }
  | {
      nodeType: 'template'
      id: BuildTypeId
      fullPath?: string
      group?: NodeGroup
    }
  | {
      nodeType: 'pipeline'
      id: PipelineId
      fullPath?: string
      parentId?: ProjectId | null | undefined
      internalId?: BuildTypeInternalId | null | undefined
      group?: NodeGroup
    }
  | {
      nodeType: 'job'
      id: string
      fullPath?: string
      group?: NodeGroup
    }
  | {
      nodeType: 'jobBatch'
      id: BuildTypeId
      jobId: string
      batchId?: string
      fullPath?: string
      group?: NodeGroup
    }
export const cloneNode = (node: ProjectOrBuildTypeNode): ProjectOrBuildTypeNode => {
  switch (node.nodeType) {
    case 'all':
      return {
        nodeType: 'all',
      }

    case 'project':
      return {
        nodeType: 'project',
        id: node.id,
        fullPath: node.fullPath,
        group: node.group,
      }

    case 'pipeline':
      return {
        nodeType: 'pipeline',
        id: node.id,
        fullPath: node.fullPath,
        group: node.group,
      }

    case 'bt':
      return {
        nodeType: 'bt',
        id: node.id,
        fullPath: node.fullPath,
        group: node.group,
      }

    case 'template':
      return {
        nodeType: 'template',
        id: node.id,
        fullPath: node.fullPath,
        group: node.group,
      }

    default:
      return {
        nodeType: 'all',
      }
  }
}
export type ActiveEntityProps = {
  projectId?: ProjectId | null | undefined
  buildTypeId?: BuildTypeId | null | undefined
  pipelineId?: PipelineId | null | undefined
  runId?: string | null | undefined
  jobId?: string | null | undefined
  batchId?: string | null | undefined
  buildId?: BuildId | null | undefined
  agentId?: AgentId | null | undefined
  agentPoolId?: AgentPoolId | null | undefined
  agentTypeId?: AgentTypeId | null | undefined
  testId?: TestId | null | undefined
  isFavorites?: boolean
}
export type ActiveEntityURLProps = Omit<ActiveEntityProps, 'buildId'> & {
  buildId?: (BuildId | null | undefined) | string
  isAllProjects?: boolean
}
export const getBuildTypeFilter: (arg0: BuildTypeId) => ProjectOrBuildTypeNode = memoize(
  (id: BuildTypeId): ProjectOrBuildTypeNode => ({
    nodeType: 'bt',
    id,
  }),
)
export const getPipelineFilter: (pipelineId: PipelineId) => ProjectOrBuildTypeNode = memoize(
  (id: PipelineId): ProjectOrBuildTypeNode => ({
    nodeType: 'pipeline',
    id,
  }),
)
export const getJobFilter: (jobId: string) => ProjectOrBuildTypeNode = memoize(
  (id: string): ProjectOrBuildTypeNode => ({
    nodeType: 'job',
    id,
  }),
)
export const getBatchFilter: ({
  id,
  jobId,
  batchId,
}: {
  id: BuildTypeId
  jobId: string
  batchId?: string
}) => ProjectOrBuildTypeNode = memoize(
  ({id, jobId, batchId}): ProjectOrBuildTypeNode => ({
    nodeType: 'jobBatch',
    jobId,
    id,
    batchId,
  }),
)
export const getProjectFilter: (projectId: ProjectId) => ProjectOrBuildTypeNode = memoize(
  (id: ProjectId): ProjectOrBuildTypeNode => ({
    nodeType: 'project',
    id,
  }),
)
export const getProjectBuildTypeFilter: (
  props: ActiveEntityProps,
) => ProjectOrBuildTypeNode | null = ({projectId, pipelineId, jobId, batchId, buildTypeId}) => {
  if (buildTypeId != null && jobId != null && batchId != null) {
    return getBatchFilter({id: buildTypeId, jobId, batchId})
  }

  if (jobId != null) {
    return getJobFilter(jobId)
  }

  if (pipelineId != null) {
    return getPipelineFilter(pipelineId)
  }

  if (buildTypeId != null) {
    return getBuildTypeFilter(buildTypeId)
  }

  if (projectId != null) {
    return getProjectFilter(projectId)
  }

  return null
}
export const STAR_TAG = '.teamcity.star'
export const STARRED_LOCATOR = `tag:(private:true,owner:current,condition:(value:${STAR_TAG},matchType:equals,ignoreCase:false))`
export const stateLocators: Record<string, string> = {
  all: '',
  queued: 'state:queued',
  running: 'state:running',
  runningAndFinished: 'state:(running:true,finished:true)',
  failedToStart: 'failedToStart:true',
  canceled: 'canceled:true',
  successful: 'status:SUCCESS',
  failed: 'status:FAILURE,failedToStart:false,canceled:false',
  pinned: 'pinned:true',
  starred: STARRED_LOCATOR,
}
export type LocatorOptions = {
  rawLocator?: string | null | undefined
  projectBuildtype?: ProjectOrBuildTypeNode | null | undefined
  useRawLocator?: boolean | null | undefined
  agent?: AgentFilter | null | undefined
  agentPattern?: string | null | undefined
  buildNamePattern?: string | null | undefined
  tag?: string | null | undefined
  buildState?: string | undefined
  baseLocator?: string | null | undefined
  withRunningAndQueued?: boolean | null | undefined
  branch?: Branch | null | undefined
  pickHistory?: boolean | null | undefined
  runningAndFinished?: boolean
  onlyFavorites?: boolean
  runningCount?: number
  queuedCount?: number
  finishedCount?: number
}
export type BuildLineLayout = {
  showAgent?: boolean
  showDuration?: boolean
  showExactDuration?: boolean
  showBranch?: boolean
  showComment?: boolean
  showPath?: boolean
  showTags?: boolean
  artifactDependenciesMode?: boolean
}
type DslOption = {
  version: string
  portable: boolean
  current?: boolean
}
export type DslOptionsResponse = {kotlin: ReadonlyArray<DslOption>}
export type DslOptions = {
  show: boolean
  version: string
  portable: boolean
}
export type RingSelectAnchorProps = CustomAnchorProps
export enum InvestigationStates {
  TAKEN = 'TAKEN',
  FIXED = 'FIXED',
  GIVEN_UP = 'GIVEN_UP',
  NONE = 'NONE',
}
export enum ResolutionType {
  WHEN_FIXED = 'whenFixed',
  MANUALLY = 'manually',
  AT_TIME = 'atTime',
}
export type Investigation = {
  id: InvestigationId
  state: InvestigationStates
  assignee: User
  resolution: {
    type?: ResolutionType
  }
  scope?: Readonly<{
    project?: {
      id: ProjectId
      name: string
    }
    buildTypes?: {
      buildType: Array<{
        id: BuildTypeId
        name: string
      }>
    }
  }>
  assignment: Readonly<{
    timestamp: string
    text?: string
    user?: User
  }>
}
export type BranchWithBuilds = Branch & {
  buildTypeId?: BuildTypeId
}
export type NormalizedBranchWithBuilds = Branch & {
  builds: {
    build: Array<BuildId>
  }
}
export type Tab = {
  id: TabId
  title: string
  onlyClassicUI?: boolean
  url?: string
  href?: string
  sakura?: boolean
  js?: Array<string>
  css?: Array<string>
}
export type TabParams = {
  buildId?: BuildId | null | undefined
  buildTypeId?: BuildTypeId | null | undefined
  projectId?: ProjectId | null | undefined
  pipelineId?: PipelineId | null | undefined
  runId?: string | null | undefined
  jobId?: string | null | undefined
  batchId?: string | null | undefined
  changeId?: ChangeId | null | undefined
  isEdit?: boolean
  agentId?: AgentId
  agentTypeId?: AgentTypeId
  blockUpdate?: boolean
  branch?: Branch | null | undefined
  personal?: boolean
  skip?: boolean
}
export type TabParamsKey = string
export const AGENTS_TAB_PARAMS_KEY: TabParamsKey = 'agentsDashboard=true'
export type Enhancer<AddProps, OwnProps> = HOC<OwnProps & AddProps, OwnProps>
export type ServerInfo = {
  licenseModeName?: string
  licenseModeDetails?: string
  version?: string
  licenseIsCloud?: boolean | undefined
  licenseIsEnterpise?: boolean | undefined
  buildNumber?: string
}
export type SnapshotDependenciesIDList = KeyValue<BuildId, ReadonlyArray<BuildId>>
export type Dialog = {
  id?: string
  type?: string
  opened?: boolean
  processing?: boolean
  error?: boolean
  data: unknown
}

export type BuildTypeHierarchy =
  | {
      type: 'PROJECT'
      name: string | undefined
      id: ProjectId
      children: Array<BuildTypeHierarchy>
    }
  | {
      type: 'BUILD_TYPE'
      name: string | undefined
      id?: BuildTypeId
    }
  | {
      type: 'TEMPLATE'
      name: string | undefined
      id?: BuildTypeId
      fullPath?: string
    }

export type RingTag = TagType

export enum KillOperationKind {
  CANCEL = 1,
  STOP = 2,
  REMOVE = 3,
}

export enum ChangesType {
  CHANGES = 'changes',
  ARTIFACTS = 'artifacts',
}
export type FilterOptions = {
  changesType?: ChangesType | null | undefined
  versionedSettings?: boolean
  userId?: UserId | string | null | undefined
  username?: string | null | undefined
  comment?: string
  path?: string
  revision?: string
  fromBuildNumber?: string
  toBuildNumber?: string
  projectId?: ProjectId | null | undefined
  showDependenciesChanges?: boolean | null
}
export enum BuildStatusType {
  SUCCESS = 'success',
  FAILURE = 'failure',
  CANCELED = 'canceled',
  QUEUED = 'queued',
}

/* eslint-disable @typescript-eslint/no-magic-numbers */
export const SUPER_USER_ID: UserId = toUserId(-42)
export const GUEST_ID: UserId = toUserId(-1000)
